/**
 * Audit Log Type Definitions
 *
 * Defines all types related to audit logs for the Admin Dashboard.
 * These types mirror the backend AuditLogEntry interface.
 */

/**
 * Audit Log Category Enum
 * Categorizes audit log entries by domain
 */
export type AuditLogCategory =
  | 'authentication'
  | 'authorization'
  | 'user_profile'
  | 'restaurant'
  | 'basket'
  | 'order'
  | 'payment'
  | 'loyalty'
  | 'vehicle'
  | 'admin'
  | 'system'
  | 'product';

/**
 * Audit Log Severity Enum
 * Indicates the importance level of the log entry
 */
export type AuditLogSeverity = 'info' | 'warning' | 'error' | 'critical';

/**
 * Audit Log Sort By Options
 */
export type AuditLogSortBy = 'occurredAt' | 'severity';

/**
 * Sort Order Options
 */
export type SortOrder = 'asc' | 'desc';

/**
 * Cache Status Type
 * Indicates whether the request was served from cache or fetched from API
 */
export type CacheStatus = 'hit' | 'miss' | null;

/**
 * Cache Status Filter Type
 * Used for filtering (includes 'na' for null values)
 */
export type CacheStatusFilter = 'hit' | 'miss' | 'na';

/**
 * Cache Status Display Labels
 */
export const CACHE_STATUS_LABELS: Record<string, string> = {
  hit: 'HIT',
  miss: 'MISS',
  na: 'N/A',
};

/**
 * Cache Status Color Classes
 * Tailwind classes for cache status badges
 */
export const CACHE_STATUS_COLORS: Record<string, string> = {
  hit: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  miss: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
  na: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
};

/**
 * Complete Audit Log Entry Interface
 * Contains all 27 fields from the backend
 */
export interface AuditLog {
  /** Unique identifier for the audit log entry */
  id: string;
  /** Type of event (e.g., "user.login", "order.created") */
  eventType: string;
  /** Category of the event */
  category: AuditLogCategory;
  /** Severity level of the event */
  severity: AuditLogSeverity;
  /** User ID who triggered the event (if applicable) */
  userId?: string | null;
  /** Admin ID who triggered the event (if applicable) */
  adminId?: string | null;
  /** IP address of the request */
  ipAddress: string;
  /** User agent string from the request */
  userAgent?: string | null;
  /** Type of resource affected (e.g., "user", "order") */
  resourceType?: string | null;
  /** ID of the affected resource */
  resourceId?: string | null;
  /** Action performed (e.g., "create", "update", "delete") */
  action: string;
  /** HTTP method used */
  httpMethod?: string | null;
  /** API endpoint called */
  endpoint?: string | null;
  /** HTTP status code of the response */
  statusCode?: number | null;
  /** Response time in milliseconds */
  responseTimeMs?: number | null;
  /** Request payload data */
  requestData?: Record<string, unknown> | null;
  /** Response payload data */
  responseData?: Record<string, unknown> | null;
  /** Additional metadata */
  metadata?: Record<string, unknown> | null;
  /** Old value (for update operations) */
  oldValue?: Record<string, unknown> | null;
  /** New value (for update operations) */
  newValue?: Record<string, unknown> | null;
  /** Session ID */
  sessionId?: string | null;
  /** Correlation ID for tracking related events */
  correlationId: string;
  /** Timestamp when the event occurred */
  occurredAt: string; // ISO 8601 date string
  /** Timestamp when the log was archived (if applicable) */
  archivedAt?: string | null; // ISO 8601 date string
  /** Cache status - indicates if the request was served from cache or API */
  cacheStatus?: CacheStatus;
}

/**
 * Audit Log Filters Interface
 * Query parameters for filtering audit logs
 */
export interface AuditLogFilters {
  /** Filter by user ID */
  userId?: string;
  /** Filter by category */
  category?: AuditLogCategory | AuditLogCategory[];
  /** Filter by severity */
  severity?: AuditLogSeverity | AuditLogSeverity[];
  /** Filter by event type */
  eventType?: string;
  /** Filter by API endpoint */
  endpoint?: string;
  /** Filter by start date (ISO 8601) */
  startDate?: string;
  /** Filter by end date (ISO 8601) */
  endDate?: string;
  /** Filter by resource type */
  resourceType?: string;
  /** Filter by resource ID */
  resourceId?: string;
  /** Filter by correlation ID (for tracing related events) */
  correlationId?: string;
  /** Filter by IP address */
  ipAddress?: string;
  /** Page number (1-indexed) */
  page?: number;
  /** Number of items per page */
  limit?: number;
  /** Sort by field */
  sortBy?: AuditLogSortBy;
  /** Sort order */
  sortOrder?: SortOrder;
  /** Filter by cache status (hit, miss, na for null) */
  cacheStatus?: CacheStatusFilter[];
}

/**
 * Pagination Metadata Interface
 */
export interface PaginationMeta {
  /** Total number of records */
  total: number;
  /** Current page number (1-indexed) */
  page: number;
  /** Number of items per page */
  limit: number;
  /** Total number of pages */
  totalPages: number;
}

/**
 * Severity Counts Interface
 * Breakdown of audit logs by severity level
 */
export interface SeverityCounts {
  /** Count of info level logs */
  info: number;
  /** Count of warning level logs */
  warning: number;
  /** Count of error level logs */
  error: number;
  /** Count of critical level logs */
  critical: number;
}

/**
 * Audit Log Summary Response
 * Returned by GET /summary — total count + severity breakdown
 */
export interface AuditLogSummary {
  /** Total matching rows, capped at 10,000 when `isCapped` is true (render as "10,000+"). */
  total: number;
  /** True when the backend capped the count at 10,000 — render as "10,000+" */
  isCapped: boolean;
  /**
   * Per-severity counts over the same scan that produced `total`. Exact when
   * `isCapped` is false; lower bounds over the first 10,000 matched rows when
   * it is true, so render them with a "+" rather than as complete totals.
   * They can still sum to slightly more than `total`, because the scan reads
   * one row past the cap in order to detect it.
   */
  severityCounts: SeverityCounts;
}

/**
 * Paginated Audit Logs Response Interface
 */
export interface PaginatedAuditLogs {
  /** Array of audit log entries */
  data: AuditLog[];
  /** Pagination metadata */
  pagination: PaginationMeta;
  // severityCounts removed — now comes from AuditLogSummary via /summary endpoint
}

/**
 * Audit Log Category Display Names
 * Human-readable labels for categories
 */
export const AUDIT_LOG_CATEGORY_LABELS: Record<AuditLogCategory, string> = {
  authentication: 'Authentication',
  authorization: 'Authorization',
  user_profile: 'User Profile',
  restaurant: 'Restaurant',
  basket: 'Basket',
  order: 'Order',
  payment: 'Payment',
  loyalty: 'Loyalty',
  vehicle: 'Vehicle',
  admin: 'Admin',
  system: 'System',
  product: 'Product',
};

/**
 * Audit Log Severity Display Names
 * Human-readable labels for severity levels
 */
export const AUDIT_LOG_SEVERITY_LABELS: Record<AuditLogSeverity, string> = {
  info: 'Info',
  warning: 'Warning',
  error: 'Error',
  critical: 'Critical',
};

/**
 * Audit Log Category Color Classes
 * Tailwind classes for category badges
 */
export const AUDIT_LOG_CATEGORY_COLORS: Record<AuditLogCategory, string> = {
  authentication: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  authorization: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
  user_profile: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  restaurant: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
  basket: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  order: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300',
  payment: 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-300',
  loyalty: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-300',
  vehicle: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-300',
  admin: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  system: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300',
  product: 'bg-lime-100 text-lime-800 dark:bg-lime-900 dark:text-lime-300',
};

/**
 * Audit Log Severity Color Classes
 * Tailwind classes for severity badges
 */
export const AUDIT_LOG_SEVERITY_COLORS: Record<AuditLogSeverity, string> = {
  info: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  error: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
  critical: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
};

/**
 * Status Code Color Classes
 * Tailwind classes for HTTP status codes
 */
export function getStatusCodeColor(statusCode: number | null | undefined): string {
  if (!statusCode) return 'text-gray-500';
  if (statusCode >= 200 && statusCode < 300) return 'text-green-600 dark:text-green-400';
  if (statusCode >= 300 && statusCode < 400) return 'text-blue-600 dark:text-blue-400';
  if (statusCode >= 400 && statusCode < 500) return 'text-orange-600 dark:text-orange-400';
  if (statusCode >= 500) return 'text-red-600 dark:text-red-400';
  return 'text-gray-500';
}

/**
 * Audit Log Tab Type
 * Defines the available tabs in the dashboard
 */
export type AuditLogTab = 'application' | 'admin';

/**
 * Tab Display Labels
 * Human-readable labels for each tab
 */
export const AUDIT_LOG_TAB_LABELS: Record<AuditLogTab, string> = {
  application: 'Application Logs',
  admin: 'Admin Logs',
};

/**
 * Category Groups by Tab
 * Defines which categories belong to each tab
 */
export const APPLICATION_LOG_CATEGORIES: AuditLogCategory[] = [
  'authentication',
  'authorization',
  'user_profile',
  'restaurant',
  'basket',
  'order',
  'payment',
  'loyalty',
  'vehicle',
  'product',
  // Note: 'system' moved to admin logs as it contains operational/admin events
];

export const ADMIN_LOG_CATEGORIES: AuditLogCategory[] = ['admin', 'system'];

/**
 * Get categories for a specific tab
 * @param tab - The tab to get categories for
 * @returns Array of categories for the specified tab
 */
export function getCategoriesForTab(tab: AuditLogTab): AuditLogCategory[] {
  return tab === 'admin' ? ADMIN_LOG_CATEGORIES : APPLICATION_LOG_CATEGORIES;
}
